import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { SalarySlip, SalarySlipStatus } from '../types';
import { salaryService } from '../services/salary.service';
import { formatINR, formatINRInt } from '../utils/formatINR';
import PayslipDocument from '../components/payslip/PayslipDocument';
import { usePayslipPdf } from '../components/payslip/usePayslipPdf';

const INK = '#0A0A0A';
const MUTED = '#737373';
const HAIRLINE = '#E5E5E5';
const BG = '#FAFAFA';

const monthLabel = (yyyymm: string): string => {
  const [y, m] = yyyymm.split('-');
  const idx = Math.max(0, Math.min(11, parseInt(m || '1', 10) - 1));
  const names = ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${names[idx]} ${y}`;
};

const STATUS_LABEL: Record<SalarySlipStatus, { label: string; bg: string; fg: string }> = {
  [SalarySlipStatus.DRAFT]: { label: 'Draft', bg: '#F4F4F5', fg: MUTED },
  [SalarySlipStatus.GENERATED]: { label: 'Pending review', bg: '#FFFBEB', fg: '#92400E' },
  [SalarySlipStatus.PENDING_APPROVAL]: { label: 'Pending review', bg: '#FFFBEB', fg: '#92400E' },
  [SalarySlipStatus.SENT]: { label: 'Sent', bg: '#EFF6FF', fg: '#1E40AF' },
  [SalarySlipStatus.PAID]: { label: 'Paid', bg: '#F0FDF4', fg: '#166534' },
};

export default function MyPayslips() {
  const [slips, setSlips] = useState<SalarySlip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await salaryService.getMySalarySlips();
        if (!mounted) return;
        const sorted = [...res.salarySlips].sort((a, b) =>
          `${b.year}-${b.month}`.localeCompare(`${a.year}-${a.month}`)
        );
        setSlips(sorted);
        if (sorted[0]) setSelectedId(sorted[0].salarySlipId);
      } catch (err: any) {
        if (mounted) setError(err?.message || 'Failed to load payslips');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const selected = useMemo(
    () => slips.find((s) => s.salarySlipId === selectedId) ?? null,
    [slips, selectedId]
  );

  const { ref: pdfRef, download: downloadPdf } = usePayslipPdf({ slip: selected });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress size={32} sx={{ color: INK }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (slips.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: MUTED }}>
        <DescriptionOutlinedIcon sx={{ fontSize: 48, color: HAIRLINE, mb: 1 }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: INK }}>
          No payslips yet
        </Typography>
        <Typography sx={{ fontSize: 13 }}>
          Your payslips will appear here once HR generates them.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.4, color: INK }}>
          My Payslips
        </Typography>
        <Typography sx={{ fontSize: 12, color: MUTED }}>
          {slips.length} payslip{slips.length === 1 ? '' : 's'} on file. Click any month to view.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 2 }}>
        {/* Left rail — month list */}
        <Box>
          <Stack gap={0.5}>
            {slips.map((s) => {
              const sel = s.salarySlipId === selectedId;
              const status = STATUS_LABEL[s.status];
              return (
                <Box
                  key={s.salarySlipId}
                  onClick={() => setSelectedId(s.salarySlipId)}
                  sx={{
                    cursor: 'pointer',
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${sel ? INK : HAIRLINE}`,
                    bgcolor: sel ? INK : '#FFFFFF',
                    color: sel ? '#FFFFFF' : INK,
                    transition: 'all 120ms',
                    '&:hover': { borderColor: INK },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                      {monthLabel(s.month)}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.75,
                        bgcolor: sel ? 'rgba(255,255,255,0.15)' : status.bg,
                        color: sel ? '#FFFFFF' : status.fg,
                      }}
                    >
                      {status.label}
                    </Box>
                  </Stack>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                    {formatINRInt(s.netPay)}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: sel ? '#D4D4D8' : MUTED, mt: 0.25 }}>
                    Gross {formatINRInt(s.grossEarnings)} · Deductions {formatINRInt(s.totalDeductions)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Right pane — selected payslip */}
        <Box>
          {selected && (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  px: 2,
                  py: 1.5,
                  mb: 1.5,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 1,
                  bgcolor: '#FFFFFF',
                }}
                className="payslip-toolbar"
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, color: INK }}>
                    {monthLabel(selected.month)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: MUTED }}>
                    Net pay {formatINR(selected.netPay)} · Status {STATUS_LABEL[selected.status].label}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={downloadPdf}
                  sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
                >
                  Download PDF
                </Button>
              </Stack>
              <Box sx={{ bgcolor: BG, p: 3, borderRadius: 1 }}>
                <PayslipDocument ref={pdfRef} slip={selected} />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
